import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  GOOGLE_AUTH_B2B_BASE_URL,
  MICROSOFT_AUTH_B2B_BASE_URL,
  SSO_BASE_URL
} from '../../api/constants';
import DokaiIcon from '../../assets/dokai-icon.svg';
import {
  login,
  setCredentials,
  sendEmailOTP,
  sendMagicLoginLink,
  verifyEmailOTP,
  resolveEmailToOrganizationAuthInfo
} from '../../api/userProfile';
import { GOOGLE_ACCOUNT_TYPE } from '../../types/userCredentials';
import { useCookies } from 'react-cookie';
import { useSnackbar } from '../../context/SnackbarContext';
import {
  setGoogleCredentials,
  useUserContextController
} from '../../context/UserContext';
import {
  getPostLoginPath,
  URL_PATHNAMES
} from '../../components/routes/AppRoutes';
import * as Sentry from '@sentry/react';
import { getSecureCookieOptions } from '../../utils/SessionCookie';
import GoogleSigninButton from './GoogleSigninButton';
import MicrosoftSigninButton from './MicrosoftSigninButton';
import EmailOTPVerification from './EmailOTPVerification';
import MagicLinkCheckEmail from './MagicLinkCheckEmail';
import WelcomeModal from '../modal/WelcomeModal';
import EmailLoginTile from './EmailLoginTile';
import Loading from '../loading';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * One-time Stytch OAuth tokens must only be exchanged once. React Strict Mode remounts
 * the tree and resets component refs, so a ref-based guard is not enough — this Set
 * survives remounts within the same JS load and prevents duplicate /signin/oauth calls.
 */
const stytchOAuthTokenExchangeStarted = new Set<string>();

/** Flag to disable OAuth buttons for now. */
const SHOW_SSO_DISCOVERY_LOGIN_BUTTONS = false;

const LoginComponent = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [, setCookie] = useCookies(['user_session_token']);
  const [, userDispatch] = useUserContextController();
  const [emailLoading, setEmailLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [impersonationLoading, setImpersonationLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [openModal, setOpenModal] = useState(false);
  const [magicLinkFollowUp, setMagicLinkFollowUp] = useState<
    null | 'check_email' | 'waitlist'
  >(null);
  const [otpStep, setOtpStep] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const icon = DokaiIcon;

  const extractParams = (url: string) => {
    const urlQueryParams = new URLSearchParams(url);
    return {
      stytch_token_type: urlQueryParams.get('stytch_token_type') ?? '',
      token: urlQueryParams.get('token') ?? '',
      stytch_redirect_type: urlQueryParams.get('stytch_redirect_type') ?? ''
    };
  };
  const urlParams = useMemo(() => extractParams(search), [search]);
  const onSignInRef = useRef<
    (token: string, stytch_token_type: string) => void
  >(() => {});

  function onSignIn(token: string, stytch_token_type: string) {
    if (token) {
      login(token, stytch_token_type)
        .then((loginData: any) => {
          const userData = loginData.user;
          const sessionData = loginData.session;
          const accountType = GOOGLE_ACCOUNT_TYPE.WEB_APP;
          if (sessionData.session_token) {
            const userCredentials = {
              user_id: String(userData.user_id),
              credentials: {
                google: {
                  access_token: sessionData.google_oauth_access_token,
                  refresh_token: sessionData.google_oauth_refresh_token
                }
              }
            };

            setCredentials(accountType, userCredentials)
              .then(() => {
                setCookie(
                  'user_session_token',
                  sessionData.session_token,
                  getSecureCookieOptions()
                );
                setGoogleCredentials(userDispatch, {
                  access_token: sessionData.google_oauth_access_token,
                  refresh_token: sessionData.google_oauth_refresh_token
                });
                showSnackbar('Login Successful', 'success');
                navigate(getPostLoginPath(stytch_token_type), {
                  replace: true
                });
              })
              .catch(() => {
                showSnackbar('Login failed', 'error');
                setGoogleLoading(false);
                setImpersonationLoading(false);
                Sentry.captureException(
                  new Error('Failed to set user credentials on login')
                );
              });
          }
        })
        .catch((error: any) => {
          setGoogleLoading(false);
          setImpersonationLoading(false);
          const errorMessage = error?.message || 'Login failed';
          if (errorMessage.includes('No affiliated organization found')) {
            setOpenModal(true);
          } else {
            showSnackbar(errorMessage, 'error');
            return;
          }
        });
    } else {
      setGoogleLoading(false);
      setImpersonationLoading(false);
      showSnackbar('Failed to login, token is NULL', 'error');
    }
  }

  onSignInRef.current = onSignIn;

  useLayoutEffect(() => {
    const { token, stytch_token_type } = urlParams;

    const looksLikeIntentionalStytchRedirect =
      token &&
      [
        'multi_tenant_impersonation',
        'discovery_oauth',
        'sso',
        'discovery'
      ].includes(stytch_token_type) &&
      typeof token === 'string';

    if (!looksLikeIntentionalStytchRedirect) return;

    if (stytchOAuthTokenExchangeStarted.has(token)) {
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    stytchOAuthTokenExchangeStarted.add(token);

    // Strip query ASAP so remounts / Router don't keep seeing a consumable token in the URL.
    window.history.replaceState({}, '', window.location.pathname);

    setImpersonationLoading(true);
    onSignInRef.current(token, stytch_token_type);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when URL search changes; onSignIn via ref
  }, [urlParams]);

  const onGoogleClick = () => {
    setGoogleLoading(true);
    // All redirect url should be registered in Stytch dashboard
    const redirectUrl = `${window.location.origin}${URL_PATHNAMES.LOGIN}`;

    const BASE_URL = GOOGLE_AUTH_B2B_BASE_URL;
    const publicToken = process.env.REACT_APP_STYTCH_PUBLIC_TOKEN;
    window.location.href = `${BASE_URL}?public_token=${publicToken}&discovery_redirect_url=${redirectUrl}&provider_prompt=select_account`;
  };

  const onMicrosoftClick = () => {
    setMicrosoftLoading(true);
    // All redirect url should be registered in Stytch dashboard
    const redirectUrl = `${window.location.origin}${URL_PATHNAMES.LOGIN}`;

    const BASE_URL = MICROSOFT_AUTH_B2B_BASE_URL;
    const publicToken = process.env.REACT_APP_STYTCH_PUBLIC_TOKEN;
    window.location.href = `${BASE_URL}?public_token=${publicToken}&discovery_redirect_url=${redirectUrl}&provider_prompt=select_account`;
  };

  const startSsoRedirect = (connectionId: string) => {
    const publicToken = process.env.REACT_APP_STYTCH_PUBLIC_TOKEN;
    const redirectUrl = `${window.location.origin}${URL_PATHNAMES.LOGIN}`;
    const qs = new URLSearchParams({
      connection_id: connectionId,
      public_token: publicToken,
      login_redirect_url: redirectUrl,
      signup_redirect_url: redirectUrl
    });

    window.location.href = `${SSO_BASE_URL}?${qs.toString()}`;
  };

  if (magicLinkFollowUp) {
    return (
      <MagicLinkCheckEmail
        email={userEmail}
        variant={magicLinkFollowUp}
        onBack={() => {
          setMagicLinkFollowUp(null);
          setEmailLoading(false);
        }}
      />
    );
  }

  if (otpStep) {
    return (
      <EmailOTPVerification
        email={userEmail}
        loading={emailLoading}
        onOTPSubmit={(otp: string) => {
          setEmailLoading(true);
          verifyEmailOTP(userEmail, otp)
            .then((data) => {
              // If a session token is returned, set it.
              if (data && data.session && data.session.session_token) {
                setCookie(
                  'user_session_token',
                  data.session.session_token,
                  getSecureCookieOptions()
                );
                showSnackbar('Login Successful', 'success');
                navigate(URL_PATHNAMES.CAMPAIGN, { replace: true });
              }
            })
            .catch((err) => {
              showSnackbar(err.message || 'OTP verification failed', 'error');
            })
            .finally(() => {
              setEmailLoading(false);
            });
        }}
        onBack={() => {
          setOtpStep(false);
          setEmailLoading(false);
        }}
      />
    );
  }

  if (
    googleLoading ||
    emailLoading ||
    microsoftLoading ||
    impersonationLoading
  ) {
    return (
      <Box
        sx={{
          maxHeight: '20vh',
          maxWidth: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          borderRadius: '12px'
        }}
      >
        <Loading message="Logging in..." />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        borderRadius: '12px'
      }}
    >
      <img
        src={icon}
        alt="dokai-icon"
        style={{
          height: '140px',
          width: '140px',
          marginBottom: '20px'
        }}
      />
      <EmailLoginTile
        loading={emailLoading}
        disabled={googleLoading || emailLoading || microsoftLoading}
        onSubmit={async (email: string) => {
          setUserEmail(email);
          setEmailLoading(true);
          try {
            const { organization_id, connection_id, email_otp_enabled } =
              await resolveEmailToOrganizationAuthInfo(email);

            if (organization_id === null) {
              const { status } = await sendMagicLoginLink(email);
              setMagicLinkFollowUp(
                status === 'waitlist' ? 'waitlist' : 'check_email'
              );
              return;
            }

            if (connection_id) {
              startSsoRedirect(connection_id);
              return;
            }

            if (email_otp_enabled) {
              await sendEmailOTP(email);
              showSnackbar('OTP sent', 'success');
              setOtpStep(true);
              return;
            } else {
              throw new Error('Access Denied');
            }
          } catch (err: any) {
            const msg = err?.message || 'Login failed';
            if (msg.includes('No affiliated organization found')) {
              setOpenModal(true);
            }
            showSnackbar(msg, 'error');
          } finally {
            setEmailLoading(false);
          }
        }}
        sx={{ mb: '10px' }}
      />

      {SHOW_SSO_DISCOVERY_LOGIN_BUTTONS && (
        <>
          <GoogleSigninButton
            loading={googleLoading}
            disabled={googleLoading || emailLoading || microsoftLoading}
            onClick={onGoogleClick}
            sx={{ mb: '10px' }}
          />
          <MicrosoftSigninButton
            loading={microsoftLoading}
            onClick={onMicrosoftClick}
          />
        </>
      )}
      <WelcomeModal
        modalTitle="No affiliated organization found"
        modalContent="Email support@dokai.ai to register your company with us"
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
    </Box>
  );
};

export default LoginComponent;
